import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Icon, SemanticCOLORS, SemanticICONS } from 'semantic-ui-react';
import { getMemberId, getSelectedAssessmentPeriodId, isCurrentMemberTeamLeader } from 'common/selectors/auth';
import MobileLeaderboardPointsHistoryChart from './MobileLeaderboardPointsHistoryChart';
import { useApiPortalV1AssessmentsLeaderboardEventsListQuery } from 'store/generated/portal/assessments/core';
import { useGetTeamSnapshotLeadersQuery } from '../../../areas/leaderboard/api/teamLeaderboardApi';
import { MobilePageContent, MobilePageSection, Title, SectionHeader, SectionDescription, MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';

interface MobileTeamLeaderLeaderboardPageProps {}

type ScoreComponents = Record<string, number | undefined>;
type ScoreRowBadge = {
  icon: SemanticICONS;
  color: SemanticCOLORS;
  borderColor: string;
  backgroundColor: string;
};

type ScoreRow = {
  title: string;
  description: string;
  points: number;
  count: number;
  badge?: ScoreRowBadge;
  isDisabled?: boolean;
};

type ScoreRowDefinition = {
  metricKey: string;
  scoreKey: string;
  title: string;
  description: (components: ScoreComponents) => string;
  badge?: ScoreRowBadge;
};

const scoreRowDefinitions: ScoreRowDefinition[] = [
  {
    metricKey: 'last_login_in_period',
    scoreKey: 'last_login_in_period_score',
    title: 'Assessment Reviewed',
    description: () => 'Logged in and reviewed team assessment',
    badge: {
      icon: 'sign in alternate',
      color: 'purple',
      borderColor: '#8b5cf6',
      backgroundColor: 'rgba(139, 92, 246, 0.12)',
    },
  },
  {
    metricKey: 'focus_plan_interventions',
    scoreKey: 'focus_plan_interventions_score',
    title: 'Focus Plans Created',
    description: () => 'Selected projects to improve team culture',
    badge: {
      icon: 'clipboard list',
      color: 'green',
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34, 197, 94, 0.12)',
    },
  },
  {
    metricKey: 'focus_plan_culture_actions',
    scoreKey: 'focus_plan_culture_actions_score',
    title: 'Actions Logged',
    description: () => 'Actions taken in support of focus plans',
    badge: {
      icon: 'star',
      color: 'orange',
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.12)',
    },
  },
  {
    metricKey: 'knowledge_base_shares',
    scoreKey: 'knowledge_base_share_score',
    title: 'Resources Shared',
    description: () => 'Shared knowledge base resources',
    badge: {
      icon: 'share',
      color: 'teal',
      borderColor: '#14b8a6',
      backgroundColor: 'rgba(20, 184, 166, 0.12)',
    },
  },
  {
    metricKey: 'video_watches',
    scoreKey: 'video_watch_score',
    title: 'Videos Watched',
    description: () => 'Video resources accessed',
    badge: {
      icon: 'play circle',
      color: 'blue',
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.12)',
    },
  },
  {
    metricKey: 'pdf_opens',
    scoreKey: 'pdf_open_score',
    title: 'Documents Viewed',
    description: () => 'Document resources accessed',
    badge: {
      icon: 'file pdf',
      color: 'red',
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.12)',
    },
  },
  {
    metricKey: 'link_opens',
    scoreKey: 'link_open_score',
    title: 'Links Viewed',
    description: () => 'Link resources accessed',
    badge: {
      icon: 'external alternate',
      color: 'violet',
      borderColor: '#7c3aed',
      backgroundColor: 'rgba(124, 58, 237, 0.12)',
    },
  },
  {
    metricKey: 'file_share_views',
    scoreKey: 'file_share_view_score',
    title: 'Shared Resources Viewed',
    description: () => 'Shared files opened by recipients',
    badge: {
      icon: 'eye',
      color: 'blue',
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.12)',
    },
  },
];

const eventTypeScoringHelp: Record<string, string> = {
  focus_plan_intervention: 'Earn points when a focus plan is created by a team leader.',
  focus_plan_intervention_reversal: 'Remove points when a previously logged focus plan is removed or reversed.',
  focus_plan_culture_action: 'Earn points when a culture action tied to a focus plan is completed.',
  focus_plan_culture_action_reversal: 'Remove points when a previously logged culture action is removed or reversed.',
  video_watch: 'Earn points for watching a video resource relevant to team culture.',
  pdf_open: 'Earn points for opening a document resource relevant to team culture.',
  link_open: 'Earn points for clicking a URL resource relevant to team culture.',
  file_share_viewed: 'Earn points when a shared file link is opened by a recipient.',
  knowledge_base_share: 'Earn points for sharing a knowledge base resource with the team.',
  login: 'Tracks a login event. Only the first login after an assessment is completed earns points.',
  first_period_login: 'Earn bonus points for the first login after an assessment is completed.',
};

const MobileTeamLeaderLeaderboardPage: React.FC<MobileTeamLeaderLeaderboardPageProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const currentMemberId = useSelector(getMemberId);

  const [activeTab, setActiveTab] = useState<'accomplishments' | 'activities'>('accomplishments');

  const selectedLeader = (location.state as { selectedLeader?: any } | undefined)?.selectedLeader ?? null;
  const selectedLeaderId = selectedLeader?.leader_id ?? null;
  const isViewingOwnData = selectedLeaderId !== null && currentMemberId !== undefined && selectedLeaderId === currentMemberId;
  const shouldHideActivitiesTab = isTeamLeader && !isViewingOwnData;

  const { data: leaders, isLoading: isLoadingLeaders, isFetching: isFetchingLeaders, error: leadersError } = useGetTeamSnapshotLeadersQuery(
    { assessmentPeriodId: selectedAssessmentPeriodId },
    {
      skip: !selectedAssessmentPeriodId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
    }
  );

  useEffect(() => {
    if (shouldHideActivitiesTab && activeTab === 'activities') {
      setActiveTab('accomplishments');
    }
  }, [shouldHideActivitiesTab, activeTab]);

  const leaderboardEventsQueryArgs = {
    limit: 200,
    offset: 0,
    ...(selectedAssessmentPeriodId ? { assessmentPeriodId: selectedAssessmentPeriodId } : {}),
  };

  const { data: leaderboardEvents, isLoading: isActivitiesLoading } = useApiPortalV1AssessmentsLeaderboardEventsListQuery(
    leaderboardEventsQueryArgs,
    {
      skip: !selectedLeaderId || !selectedAssessmentPeriodId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
    }
  );

  const selectedLeaderRows = useMemo(() => {
    if (!selectedLeader) return [] as ScoreRow[];

    const scoreComponents = (selectedLeader.score_components || {}) as ScoreComponents;

    return scoreRowDefinitions.map(definition => {
      const count = typeof scoreComponents[definition.metricKey] === 'number' ? scoreComponents[definition.metricKey]! : 0;
      const points = Number(scoreComponents[definition.scoreKey] || 0);

      return {
        title: definition.title,
        description: definition.description(scoreComponents),
        points,
        count,
        badge: definition.badge,
        isDisabled: count === 0,
      };
    });
  }, [selectedLeader]);

  const activityEvents = useMemo(() => {
    if (!selectedLeader || !leaderboardEvents?.results || !selectedAssessmentPeriodId) {
      return [] as Array<{
        event_date?: string | null;
        event_type: string;
        display_event_type?: string;
        event_label: string;
        points: number;
        leader?: { id: number } | null;
        assessment_period?: number;
      }>;
    }

    return (leaderboardEvents.results as Array<{
      event_date?: string | null;
      event_type: string;
      display_event_type?: string;
      event_label: string;
      points: number;
      leader?: { id: number } | null;
      assessment_period?: number;
    }>)
      .filter(event =>
        event.leader?.id === selectedLeader.leader_id &&
        event.assessment_period === selectedAssessmentPeriodId
      )
      .sort((a, b) => {
        const dateA = a.event_date ? new Date(a.event_date).getTime() : 0;
        const dateB = b.event_date ? new Date(b.event_date).getTime() : 0;
        return dateB - dateA;
      });
  }, [selectedLeader, leaderboardEvents, selectedAssessmentPeriodId]);

  const leaderName = selectedLeader ? `${selectedLeader.leader_first_name || ''} ${selectedLeader.leader_last_name || ''}`.trim() : 'Score breakdown';

  return (
    <MobilePageContent>
        <SectionHeader>
          <Title>{leaderName ? `${leaderName}` : 'Leaderboard detail'}</Title>
        </SectionHeader>
        <SectionDescription>Review leaderboard performance and activity</SectionDescription>
      <MobilePageSection>
        <ContentWrapper>
          {selectedLeader ? (
            <>
              <ChartSection>
                <MobileLeaderboardPointsHistoryChart weeklyPointsHistory={selectedLeader.weekly_points_history} />
              </ChartSection>

              <TabsWrapper>
                <TabItem active={activeTab === 'accomplishments'} onClick={() => setActiveTab('accomplishments')}>
                  Accomplishments
                </TabItem>
                {!shouldHideActivitiesTab && (
                  <TabItem active={activeTab === 'activities'} onClick={() => setActiveTab('activities')}>
                    Activities
                  </TabItem>
                )}
              </TabsWrapper>

              <TabContent>
                {activeTab === 'accomplishments' ? (
                  selectedLeaderRows.length > 0 ? (
                    <BreakdownTables>
                      {[selectedLeaderRows.slice(0, 4), selectedLeaderRows.slice(4)]
                        .filter(rows => rows.length > 0)
                        .map((rows, index) => (
                          <BreakdownTable key={index}>
                            <BreakdownList>
                              {rows.map(row => (
                                <BreakdownRow key={row.title} $disabled={row.isDisabled}>
                                  <BreakdownIconCell>
                                    {row.badge ? (
                                      <BreakdownBadgeIcon
                                        color={row.isDisabled ? '#6b7280' : row.badge.color}
                                        borderColor={row.isDisabled ? '#d1d5db' : row.badge.borderColor}
                                        backgroundColor={row.isDisabled ? 'rgba(156, 163, 175, 0.12)' : row.badge.backgroundColor}
                                      >
                                        <Icon name={row.badge.icon} color={row.isDisabled ? 'grey' : row.badge.color} size="large" />
                                      </BreakdownBadgeIcon>
                                    ) : null}
                                  </BreakdownIconCell>
                                  <BreakdownText>
                                  <BreakdownTextHeader>
                                    <BreakdownLabel $disabled={row.isDisabled}>{row.title}</BreakdownLabel>
                                    <ScorePill>{row.points > 0 ? `+${row.points}` : `${row.points}`}</ScorePill>
                                  </BreakdownTextHeader>
                                  <BreakdownDescription $disabled={row.isDisabled}>{row.description}</BreakdownDescription>
                                </BreakdownText>
                              </BreakdownRow>
                              ))}
                            </BreakdownList>
                          </BreakdownTable>
                        ))}
                    </BreakdownTables>
                  ) : (
                    <EmptyState>
                      <Icon name="ban" color="grey" />
                      <EmptyStateText>No accomplishments have been scored for this leader for the current period.</EmptyStateText>
                    </EmptyState>
                  )
                ) : (
                  <ActivitiesSection>
                    {isActivitiesLoading ? (
                      <MobileSpinner text="Loading activities..." size="small" />
                    ) : activityEvents.length === 0 ? (
                      <EmptyState>
                        <Icon name="history" color="grey" />
                        <EmptyStateText>No activity events available for this leader in the selected assessment period.</EmptyStateText>
                      </EmptyState>
                    ) : (
                      <ActivityTableWrapper>
                        <ActivityTable>
                          <thead>
                            <ActivityTableRowHeader>
                              <ActivityHeaderCell width="15%">Date</ActivityHeaderCell>
                              <ActivityHeaderCell width="18%">Event</ActivityHeaderCell>
                              <ActivityHeaderCell width="52%">Details</ActivityHeaderCell>
                              <ActivityHeaderCell right width="15%">Points</ActivityHeaderCell>
                            </ActivityTableRowHeader>
                          </thead>
                          <tbody>
                            {activityEvents.map((event, index) => (
                              <ActivityTableRow
                                key={`${event.event_date}-${index}`}
                                title={eventTypeScoringHelp[event.event_type] ?? undefined}
                              >
                                <ActivityCell>{event.event_date ? new Date(event.event_date).toLocaleDateString() : '—'}</ActivityCell>
                                <ActivityCell>{event.display_event_type || event.event_type}</ActivityCell>
                                <ActivityCell>{event.event_label}</ActivityCell>
                                <ActivityCell right>{event.points > 0 ? `+${event.points}` : `${event.points}`}</ActivityCell>
                              </ActivityTableRow>
                            ))}
                          </tbody>
                        </ActivityTable>
                      </ActivityTableWrapper>
                    )}
                  </ActivitiesSection>
                )}
              </TabContent>
            </>
          ) : (
            <p>No leader selected.</p>
          )}
        </ContentWrapper>
      </MobilePageSection>
    </MobilePageContent>
  );
};

export default MobileTeamLeaderLeaderboardPage;

const ContentWrapper = styled.div`
  display: grid;
  gap: 24px;
`;

const ChartSection = styled.div`
  width: 100%;
  min-height: 140px;
`;

const TabsWrapper = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const TabItem = styled.button<{ active: boolean }>`
  border: 1px solid ${props => (props.active ? '#2563eb' : '#d1d5db')};
  background: ${props => (props.active ? '#eff6ff' : '#ffffff')};
  color: ${props => (props.active ? '#1d4ed8' : '#374151')};
  border-radius: 999px;
  padding: 10px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${props => (props.active ? '#dbeafe' : '#f9fafb')};
  }
`;

const TabContent = styled.div`
  display: grid;
  gap: 24px;
`;

const BreakdownTables = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 100%;
  margin-bottom: 24px;

  @media (max-width: 960px) and (orientation: portrait) {
    grid-template-columns: 1fr;
  }
`;

const BreakdownTable = styled.div`
  display: grid;
  gap: 10px;
  background: transparent;
  border: none;
  border-radius: 16px;
  padding: 14px 0 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const BreakdownList = styled.div`
  display: grid;
  gap: 8px;
`;

const BreakdownRow = styled.div<{ $disabled?: boolean }>`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  grid-auto-rows: auto;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 12px;
  background: ${props => (props.$disabled ? '#f3f4f6' : '#ffffff')};
  border-radius: 14px;
  border: 1px solid ${props => (props.$disabled ? '#e5e7eb' : '#d1d5db')};

  @media (max-width: 520px) {
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 10px;
  }
`;

const BreakdownIconCell = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const BreakdownBadgeIcon = styled.div<{ color: string; borderColor: string; backgroundColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid ${props => props.borderColor};
  background: ${props => props.backgroundColor};
  color: ${props => props.color};
`;

const BreakdownText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  @media (max-width: 520px) {
    grid-column: 2 / -1;
  }
`;

const BreakdownTextHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;

const BreakdownLabel = styled.div<{ $disabled?: boolean }>`
  flex: 1;
  font-weight: 600;
  color: ${props => (props.$disabled ? '#6b7280' : '#111827')};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BreakdownDescription = styled.div<{ $disabled?: boolean }>`
  font-size: 13px;
  color: ${props => (props.$disabled ? '#6b7280' : '#4b5563')};
`;


const ScorePill = styled.div`
  min-width: 40px;
  padding: 4px 2px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 700;
  text-align: center;
  flex-shrink: 0;
`;

const ActivitiesSection = styled.div`
  display: grid;
  gap: 16px;
`;

const ActivityTableWrapper = styled.div`
  overflow-x: auto;
`;

const ActivityTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const ActivityTableRowHeader = styled.tr``;

const ActivityHeaderCell = styled.th<{ width?: string; right?: boolean }>`
  padding: 12px 10px;
  text-align: ${props => (props.right ? 'right' : 'left')};
  width: ${props => props.width || 'auto'};
  color: #374151;
  font-size: 13px;
  font-weight: 700;
  border-bottom: 1px solid #e5e7eb;
`;

const ActivityTableRow = styled.tr<{ title?: string }>`
  cursor: default;
  &:nth-of-type(2n) {
    background: #f9fafb;
  }
`;

const ActivityCell = styled.td<{ right?: boolean }>`
  padding: 12px 10px;
  text-align: ${props => (props.right ? 'right' : 'left')};
  color: #4b5563;
  font-size: 13px;
  vertical-align: top;
`;

const EmptyState = styled.div`
  display: grid;
  gap: 12px;
  justify-items: center;
  padding: 24px;
  border: 1px dashed #d1d5db;
  border-radius: 16px;
  color: #6b7280;
`;

const EmptyStateText = styled.div`
  font-weight: 600;
`;
